"use client";

/**
 * The Figures section — the four aggregate figures as a card of entry rows.
 *
 * Extracted from the design source `compass-oppreport.jsx` (the `figures`
 * section: `.rv-card.rv-rows`, `.rv-row.entry`, `.rv-rowval.as`,
 * `.rv-rowdesc`).
 *
 * The whole row is one control: it opens the read-only formula trace
 * (`onOpenTrace`). Editing never happens in the report, and the trace modal
 * is where a reader goes on to the assumptions table — the row itself carries
 * no second affordance, as in the design.
 */

import { ReportAssumedBadge } from "./ReportAssumedBadge";
import { buildReportFigureRows } from "./report-figures";
import { formatHorizonYears } from "./report-format";
import type {
  ReportFigureRow,
  ReportFigures,
  ReportTraceTarget,
} from "./report-types";

export interface ReportFiguresListProps {
  figures: ReportFigures;
  onOpenTrace?: (target: ReportTraceTarget) => void;
}

/**
 * The trace target for a figure.
 *
 * `id` is the `ReportFigureKey` — never a row id, and never the label. Phase 4
 * resolves `ReportData.traceNodes[target.id]`, and figures are keyed there by
 * exactly this key. `formula` and `aggregate` stay `null` because this
 * component is not given the trace nodes; the modal hydrates them from the
 * node it resolves.
 */
function figureTarget(row: ReportFigureRow): ReportTraceTarget {
  return {
    id: row.key,
    kind: "figure",
    label: row.label,
    value: row.value,
    formula: null,
    aggregate: null,
  };
}

/** `.rv-rowname` — 13.5px/500 muted label on a fixed 260px column. */
const ROW_NAME = [
  "flex-[0_0_260px] max-[860px]:flex-[0_0_160px]",
  "text-[13.5px] font-medium text-(--fg-muted)",
].join(" ");

/** `.rv-rowval.as` — right-aligned tabular mono, so the four values line up. */
const ROW_VALUE = [
  "shrink-0 whitespace-nowrap font-[family-name:var(--font-mono)]",
  "text-[16px] font-bold tabular-nums text-(--fg-primary)",
].join(" ");

/**
 * The Figures section body. Values are pre-formatted by
 * `buildReportFigureRows`, so `—` arrives here as an ordinary string: it is
 * the normal uncomputed state, not an error (plan §6 risk 2).
 */
export function ReportFiguresList({
  figures,
  onOpenTrace,
}: ReportFiguresListProps) {
  const rows = buildReportFigureRows(figures);
  const horizonLabel = formatHorizonYears(figures.horizonYears);

  return (
    <div
      className={[
        "font-[family-name:var(--font-body)] overflow-hidden mb-3",
        "bg-(--bg-white) border border-(--border-subtle)",
        "rounded-(--radius-container)",
      ].join(" ")}
    >
      {rows.map((row) => (
        <button
          key={row.key}
          type="button"
          className={[
            "grid gap-1.5 w-full text-left px-[18px] py-4",
            "border-t border-(--border-subtle) first:border-t-0",
            "transition-colors duration-300 ease-in-out",
            onOpenTrace
              ? "cursor-pointer hover:bg-(--bg-sky)"
              : "cursor-default",
          ].join(" ")}
          onClick={
            onOpenTrace ? () => onOpenTrace(figureTarget(row)) : undefined
          }
        >
          <span className="flex items-baseline gap-3">
            <span className={ROW_NAME}>{row.label}</span>
            {row.horizonAssumed && (
              <span className="ml-auto">
                <ReportAssumedBadge horizonLabel={horizonLabel} />
              </span>
            )}
            {/* `ml-auto` moves to the badge when there is one, so the value
                stays hard right either way. */}
            <span
              className={[ROW_VALUE, row.horizonAssumed ? "" : "ml-auto"].join(
                " ",
              )}
            >
              {row.value}
            </span>
          </span>
          <span className="text-[12.5px] leading-[1.5] text-(--fg-secondary)">
            {row.description}
          </span>
        </button>
      ))}
    </div>
  );
}
